import styles from "./MedalDetail.module.less";
import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getMedalDetails, gitShareModalImage } from "api/mine";
import { Toast } from "react-vant";
import CommonNavBar from "views/components/CommonNavBar";
import { showMedalShare } from "./MedalShare";

const MedalDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [detailData, setDetailData] = useState(null);
  const [currentShowLevel, setCurrentShowLevel] = useState(
    searchParams.get("show_level") || "",
  );
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchMedalDetails = async (level) => {
    Toast.loading({
      message: "加载中...",
      forbidClick: false,
      duration: 0,
    });
    try {
      const category_id = searchParams.get("category_id");
      const res = await getMedalDetails(category_id, level);
      if (res.code === 200 && res.data) {
        setDetailData(res.data);
        setCurrentShowLevel(level);
      }
    } catch (error) {
      console.error("Failed to fetch medal details:", error);
    } finally {
      Toast.clear();
    }
  };

  useEffect(() => {
    fetchMedalDetails(searchParams.get("show_level") || "");
  }, []);

  const handleNodeClick = (node) => {
    // 处理空字符串判断逻辑，如果当前状态为空时点击已高亮的节点不再触发更新
    if (isSelectedLevel(node.level)) {
      return;
    }
    if (node.level !== currentShowLevel) {
      fetchMedalDetails(node.level);
    }
  };

  const handleShare = async () => {
    const payload = {
      medal_layout: 1,
      medal_image1: detailData.icon,
      medal_text1: detailData.title,
    };

    Toast.loading({
      message: "正在生成分享...",
      forbidClick: false,
      duration: 0,
    });

    try {
      const res = await gitShareModalImage(payload);
      if (!isMounted.current) {
        Toast.clear();
        return;
      }
      Toast.clear();

      if (res.code === 200 && res.data) {
        showMedalShare(res.data);
      } else {
        Toast.fail(res.message || "分享失败");
      }
    } catch (error) {
      if (!isMounted.current) {
        Toast.clear();
        return;
      }
      console.error("Share failed:", error);
      Toast.fail(error?.message || "分享失败");
    }
  };

  const isCompleted =
    detailData && (currentShowLevel !== ""
      ? Number(currentShowLevel) <= detailData.current_level
      : detailData.is_earned === 1); // 如果 level 为空，根据 is_earned 字段判断是否完成

  const isSelectedLevel = (nodeLevel) => {
    if (!detailData) return false;
    if (currentShowLevel !== "") {
      return String(nodeLevel) === String(currentShowLevel);
    }
    // 如果为空，优先使用返回数据本身的 level，其次看 current_level，最后取第一个
    const targetLevel = detailData.level || (detailData.current_level > 0 ? detailData.current_level : detailData.progress_nodes?.[0]?.level);
    return String(nodeLevel) === String(targetLevel);
  };

  return (
    <div className={styles["medal-detail-container"]}>
      <CommonNavBar
        title=""
        onClickBack={() => navigate(-1)}
        backgroundColor="transparent"
        isTop={true}
      />
      {detailData && (
        <>
          <div className={styles["top-section"]}>
            <img
              className={styles["top-bg"]}
              src={
                isCompleted
                  ? require("@/images/mine/my-medal/detail-back-complete.png")
                  : require("@/images/mine/my-medal/detail-back.png")
              }
              alt="top bg"
            />
            <img
              className={styles["top-bg-portrait"]}
              src={
                isCompleted
                  ? require("@/images/mine/my-medal/detail-back-complete-portrait.png")
                  : require("@/images/mine/my-medal/detail-back-portrait.png")
              }
              alt="top bg portrait"
            />
            <img
              className={styles["top-bg-landscape"]}
              src={
                isCompleted
                  ? require("@/images/mine/my-medal/detail-back-complete-landscape.png")
                  : require("@/images/mine/my-medal/detail-back-landscape.png")
              }
              alt="top bg landscape"
            />
            <div className={styles["medal-icon-wrapper"]}>
              <img
                className={styles["medal-icon"]}
                src={detailData?.icon}
                alt="medal icon"
              />
            </div>
          </div>

          <div className={styles["scroll-content"]}>
            <div className={styles["progress-section"]}>
              <div className={styles["progress-line-container"]}>
                <div className={styles["progress-line-bg"]}>
                  <div
                    className={styles["progress-line-active"]}
                    style={{
                      width: `${
                        detailData.progress_nodes.length > 1
                          ? (() => {
                              let activeIndex = 0;
                              detailData.progress_nodes.forEach((node, idx) => {
                                if (node.is_reached === 1) activeIndex = idx;
                              });
                              return (
                                (Math.max(0, activeIndex) /
                                  (detailData.progress_nodes.length - 1)) *
                                100
                              );
                            })()
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
              <div className={styles["nodes-container"]}>
                {detailData?.progress_nodes?.map((node, index) => {
                  const isSelected = isSelectedLevel(node.level);
                  const isReached = node.is_reached === 1;

                  return (
                    <div
                      key={node.level}
                      className={`${styles["node-item"]} ${
                        isSelected ? styles["selected"] : ""
                      } ${isReached ? styles["reached"] : ""}`}
                      onClick={() => handleNodeClick(node)}
                    >
                      <div className={styles["dot-wrapper"]}>
                        <div className={styles["dot"]}></div>
                      </div>
                      <div className={styles["level-text"]}>{node.title_level}</div>
                    </div>
                  );
                })}
              </div>
            </div>

        <div className={styles["info-section"]}>
          <div className={styles["medal-title"]}>{detailData?.title}</div>
          <div className={styles["medal-tpl-text"]}>
            {detailData?.tpl_text &&
              detailData.tpl_text
                .split("\n")
                .map((line, i) => <div key={i}>{line}</div>)}
          </div>
          <div className={styles["medal-reach-text"]}>
            {detailData?.reach_user_text}
          </div>

          {isCompleted ? (
            <div className={styles["share-btn"]} onClick={handleShare}>
              炫耀一下
            </div>
          ) : detailData?.motivate_text ? (
            <div className={styles["motivate-text"]}>
              {detailData.motivate_text}
            </div>
          ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MedalDetail;
